import {
  PROCEDURE_MEDIA,
  type MediaLang,
  type ProcedureMediaKey,
} from "@/lib/procedure-media";
import { ImageLightbox } from "@/components/site/ImageLightbox";

interface ProcedureFigureProps {
  media: ProcedureMediaKey;
  lang?: MediaLang;
  /** Above-the-fold figures load eagerly; everything else lazy loads. */
  priority?: boolean;
  className?: string;
  imgClassName?: string;
  captionClassName?: string;
  sizes?: string;
  lightbox?: boolean;
  /** Crops the inline preview only; the lightbox retains the full image. */
  previewCropTopPercent?: number;
  previewCropBottomPercent?: number;
  /**
   * Page-specific caption wording. Falls back to the registry caption for the
   * requested language, then to the English caption.
   */
  caption?: Partial<Record<MediaLang, string>>;
}

/**
 * Renders one clinical figure (diagram, graph, or imaging study) from the
 * shared registry in src/lib/procedure-media.ts, with the alt text and
 * caption for the requested language. English and Spanish pages use the same
 * component so the imagery never drifts between the two.
 */
export function ProcedureFigure({
  media,
  lang = "en",
  priority = false,
  className = "",
  imgClassName = "w-full h-auto rounded-lg",
  captionClassName = "mt-2 text-center text-sm text-muted-foreground",
  sizes,
  lightbox = false,
  previewCropTopPercent,
  previewCropBottomPercent,
  caption,
}: ProcedureFigureProps) {
  const item: (typeof PROCEDURE_MEDIA)[ProcedureMediaKey] & {
    srcSet?: string;
    webpSrcSet?: string;
    fallbackSrc?: string;
    blurDataUrl?: string;
  } = PROCEDURE_MEDIA[media];
  const text = caption?.[lang] || item.caption[lang] || item.caption.en;
  const picture = (
    <picture>
      <source srcSet={item.webpSrcSet ?? item.webp} type="image/webp" sizes={sizes} />
      <img
        src={item.fallbackSrc ?? item.src}
        srcSet={item.srcSet}
        alt={item.alt[lang]}
        width={item.width}
        height={item.height}
        loading={priority ? "eager" : "lazy"}
        {...(priority ? {} : { fetchPriority: "low" as const })}
        decoding="async"
        sizes={sizes}
        className={
          item.blurDataUrl
            ? [imgClassName, "bg-muted", "bg-cover", "bg-center"]
                .flatMap((c) => c.split(" "))
                .filter((c, i, all) => c && all.indexOf(c) === i)
                .join(" ")
            : imgClassName
        }
        style={
          item.blurDataUrl ? { backgroundImage: `url(${item.blurDataUrl})` } : undefined
        }
      />
    </picture>
  );
  const cropTop = previewCropTopPercent ?? 0;
  const cropBottom = previewCropBottomPercent ?? 0;
  const preview = cropTop >= 0 && cropBottom >= 0 && cropTop + cropBottom > 0 && cropTop + cropBottom < 100 ? (
    <span
      className="relative block w-full overflow-hidden rounded-lg bg-muted"
      style={{ aspectRatio: `${item.width} / ${item.height * (1 - (cropTop + cropBottom) / 100)}` }}
    >
      <span
        className="absolute inset-x-0 top-0 block"
        style={{ transform: `translateY(-${cropTop}%)` }}
      >
        {picture}
      </span>
    </span>
  ) : picture;
  return (
    <figure className={className}>
      {lightbox ? (
        <ImageLightbox
          fullSrc={item.src}
          fullWidth={item.width}
          fullHeight={item.height}
          alt={item.alt[lang]}
          caption={text}
          enlargeLabel={lang === "es" ? `Ampliar imagen: ${item.alt.es}` : undefined}
          closeLabel={lang === "es" ? "Cerrar imagen ampliada" : undefined}
        >
          {preview}
        </ImageLightbox>
      ) : (
        preview
      )}
      <figcaption className={captionClassName}>{text}</figcaption>
    </figure>
  );
}

/** Three-up row of imaging studies, used for the sleeve comparison galleries. */
export function ProcedureFigureGrid({
  items,
  lang = "en",
  className = "mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3",
}: {
  items: ProcedureMediaKey[];
  lang?: MediaLang;
  className?: string;
}) {
  const thumbnailRow = items.length >= 3;
  return (
    <div className={thumbnailRow ? className.replace("grid-cols-1", "grid-cols-3") : className}>
      {items.map((key) => (
        <ProcedureFigure
          key={key}
          media={key}
          lang={lang}
          imgClassName="w-full h-auto rounded-lg bg-black"
          captionClassName="mt-1 text-xs text-muted-foreground text-center"
          sizes={thumbnailRow ? "(min-width: 1024px) 173px, (min-width: 640px) calc((100vw - 7.5rem) / 3), calc((100vw - 4.5rem) / 3)" : undefined}
          lightbox={thumbnailRow}
        />
      ))}
    </div>
  );
}
